import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Save, Plus, Trash2, Loader2, Link2, GripVertical } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

// Available icons for social links
const AVAILABLE_ICONS = [
  'Github', 'Linkedin', 'Twitter', 'Instagram', 'Facebook', 'Youtube', 
  'Twitch', 'Discord', 'Slack', 'Dribbble', 'Figma', 'Globe', 
  'Mail', 'Phone', 'MapPin', 'Calendar', 'Link', 'ExternalLink',
  'Code', 'Coffee', 'Heart', 'Star', 'Bookmark', 'Send'
];

const PortfolioEditor = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPortfolio(token);
      if (response.success) {
        setPortfolio(response.data);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load portfolio data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminAPI.updatePortfolio(portfolio, token);
      if (response.success) {
        toast({
          title: 'Success!',
          description: 'Portfolio updated successfully',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update portfolio',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateBasics = (field, value) => {
    setPortfolio({
      ...portfolio,
      basics: { ...portfolio.basics, [field]: value },
    });
  };

  const updateProfile = (index, field, value) => {
    const profiles = [...portfolio.basics.profiles];
    profiles[index] = { ...profiles[index], [field]: value };
    setPortfolio({
      ...portfolio,
      basics: { ...portfolio.basics, profiles },
    });
  };

  const addSkill = () => {
    setPortfolio({
      ...portfolio,
      skills: [...portfolio.skills, { name: '', category: 'Frontend' }],
    });
  };

  const updateSkill = (index, field, value) => {
    const skills = [...portfolio.skills];
    skills[index] = { ...skills[index], [field]: value };
    setPortfolio({ ...portfolio, skills });
  };

  const removeSkill = (index) => {
    setPortfolio({
      ...portfolio,
      skills: portfolio.skills.filter((_, i) => i !== index),
    });
  };

  const addWork = () => {
    setPortfolio({
      ...portfolio,
      work: [
        ...portfolio.work,
        {
          position: '',
          company: '',
          location: '',
          startDate: '',
          endDate: null,
          highlights: [''],
        },
      ],
    });
  };

  const updateWork = (index, field, value) => {
    const work = [...portfolio.work];
    work[index] = { ...work[index], [field]: value };
    setPortfolio({ ...portfolio, work });
  };

  const removeWork = (index) => {
    setPortfolio({
      ...portfolio,
      work: portfolio.work.filter((_, i) => i !== index),
    });
  };

  const addHighlight = (workIndex) => {
    const work = [...portfolio.work];
    work[workIndex] = {
      ...work[workIndex],
      highlights: [...(work[workIndex].highlights || []), ''],
    };
    setPortfolio({ ...portfolio, work });
  };

  const updateHighlight = (workIndex, highlightIndex, value) => {
    const work = [...portfolio.work];
    const highlights = [...work[workIndex].highlights];
    highlights[highlightIndex] = value;
    work[workIndex] = { ...work[workIndex], highlights };
    setPortfolio({ ...portfolio, work });
  };

  const removeHighlight = (workIndex, highlightIndex) => {
    const work = [...portfolio.work];
    work[workIndex] = {
      ...work[workIndex],
      highlights: work[workIndex].highlights.filter((_, i) => i !== highlightIndex),
    };
    setPortfolio({ ...portfolio, work });
  };

  // Social Links functions
  const addSocialLink = () => {
    const newLink = {
      id: Date.now().toString(),
      name: '',
      icon: 'Link',
      url: '',
      tooltip: '',
      order: (portfolio.socialLinks || []).length
    };
    setPortfolio({
      ...portfolio,
      socialLinks: [...(portfolio.socialLinks || []), newLink]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const socialLinks = [...(portfolio.socialLinks || [])];
    socialLinks[index] = { ...socialLinks[index], [field]: value };
    setPortfolio({ ...portfolio, socialLinks });
  };

  const removeSocialLink = (index) => {
    setPortfolio({
      ...portfolio,
      socialLinks: (portfolio.socialLinks || []).filter((_, i) => i !== index)
    });
  };

  const moveSocialLink = (index, direction) => {
    const socialLinks = [...(portfolio.socialLinks || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= socialLinks.length) return;
    
    [socialLinks[index], socialLinks[newIndex]] = [socialLinks[newIndex], socialLinks[index]];
    socialLinks.forEach((link, i) => link.order = i);
    setPortfolio({ ...portfolio, socialLinks });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  if (!portfolio) {
    return <div className="text-center text-gray-400 py-12">No portfolio data found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={18} />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Accordion type="single" collapsible defaultValue="basics" className="space-y-4">
        {/* Basics */}
        <AccordionItem value="basics" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-xl font-bold text-white hover:text-orange-500">
            Basic Information
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Name</Label>
                <Input
                  value={portfolio.basics.name}
                  onChange={(e) => updateBasics('name', e.target.value)}
                  className="bg-zinc-900 border-orange-500/20 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Label</Label>
                <Input
                  value={portfolio.basics.label}
                  onChange={(e) => updateBasics('label', e.target.value)}
                  className="bg-zinc-900 border-orange-500/20 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Email</Label>
                <Input
                  value={portfolio.basics.email}
                  onChange={(e) => updateBasics('email', e.target.value)}
                  className="bg-zinc-900 border-orange-500/20 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Phone</Label>
                <Input
                  value={portfolio.basics.phone}
                  onChange={(e) => updateBasics('phone', e.target.value)}
                  className="bg-zinc-900 border-orange-500/20 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Summary</Label>
              <Textarea
                value={portfolio.basics.summary}
                onChange={(e) => updateBasics('summary', e.target.value)}
                rows={4}
                className="bg-zinc-900 border-orange-500/20 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Social Profiles</Label>
              {portfolio.basics.profiles.map((profile, index) => (
                <div key={index} className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Network"
                    value={profile.network}
                    onChange={(e) => updateProfile(index, 'network', e.target.value)}
                    className="bg-zinc-900 border-orange-500/20 text-white"
                  />
                  <Input
                    placeholder="URL"
                    value={profile.url}
                    onChange={(e) => updateProfile(index, 'url', e.target.value)}
                    className="bg-zinc-900 border-orange-500/20 text-white"
                  />
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Social Links / Connect */}
        <AccordionItem value="socialLinks" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-xl font-bold text-white hover:text-orange-500">
            <div className="flex items-center gap-2">
              <Link2 size={20} className="text-orange-500" />
              Connect Links ({(portfolio.socialLinks || []).length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-4">
            <p className="text-gray-400 text-sm">Add social media links with custom icons and tooltips. These links appear in both the Contact section and Footer.</p>
            <Button
              onClick={addSocialLink}
              variant="outline"
              className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
            >
              <Plus size={18} className="mr-2" />
              Add Social Link
            </Button>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {(portfolio.socialLinks || []).map((link, index) => (
                <div key={link.id || index} className="p-4 bg-zinc-900/50 rounded-xl space-y-3 border border-orange-500/10">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveSocialLink(index, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-gray-800 rounded disabled:opacity-30"
                        >
                          <GripVertical size={14} className="text-gray-500 rotate-180" />
                        </button>
                        <button
                          onClick={() => moveSocialLink(index, 'down')}
                          disabled={index === (portfolio.socialLinks || []).length - 1}
                          className="p-1 hover:bg-gray-800 rounded disabled:opacity-30"
                        >
                          <GripVertical size={14} className="text-gray-500" />
                        </button>
                      </div>
                      <span className="text-orange-500 font-semibold">Link #{index + 1}</span>
                    </div>
                    <Button
                      onClick={() => removeSocialLink(index)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-400 text-xs">Display Name</Label>
                      <Input
                        placeholder="e.g., LinkedIn"
                        value={link.name}
                        onChange={(e) => updateSocialLink(index, 'name', e.target.value)}
                        className="bg-zinc-900 border-orange-500/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs">Icon</Label>
                      <select
                        value={link.icon}
                        onChange={(e) => updateSocialLink(index, 'icon', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-900 border border-orange-500/20 text-white rounded-md h-10"
                      >
                        {AVAILABLE_ICONS.map(icon => (
                          <option key={icon} value={icon}>{icon}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">URL</Label>
                    <Input
                      placeholder="https://linkedin.com/in/username"
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      className="bg-zinc-900 border-orange-500/20 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Tooltip (appears on hover)</Label>
                    <Input
                      placeholder="e.g., Connect with me on LinkedIn"
                      value={link.tooltip}
                      onChange={(e) => updateSocialLink(index, 'tooltip', e.target.value)}
                      className="bg-zinc-900 border-orange-500/20 text-white"
                    />
                  </div>
                </div>
              ))}
              {(!portfolio.socialLinks || portfolio.socialLinks.length === 0) && (
                <p className="text-gray-500 text-sm italic text-center py-4">
                  No social links added yet. Click "Add Social Link" to add your first link.
                </p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Skills */}
        <AccordionItem value="skills" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-xl font-bold text-white hover:text-orange-500">
            Skills ({portfolio.skills.length})
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-4">
            <Button
              onClick={addSkill}
              variant="outline"
              className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
            >
              <Plus size={18} className="mr-2" />
              Add Skill
            </Button>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {portfolio.skills.map((skill, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder="Skill name"
                    value={skill.name}
                    onChange={(e) => updateSkill(index, 'name', e.target.value)}
                    className="bg-zinc-900 border-orange-500/20 text-white"
                  />
                  <select
                    value={skill.category}
                    onChange={(e) => updateSkill(index, 'category', e.target.value)}
                    className="px-3 py-2 bg-zinc-900 border border-orange-500/20 text-white rounded-md"
                  >
                    <option value="Frontend">Frontend</option>
                    <option value="Backend">Backend</option>
                    <option value="Database">Database</option>
                    <option value="Tools">Tools</option>
                  </select>
                  <Button
                    onClick={() => removeSkill(index)}
                    variant="outline"
                    size="icon"
                    className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Work Experience */}
        <AccordionItem value="work" className="glass border border-orange-500/20 rounded-2xl px-6">
          <AccordionTrigger className="text-xl font-bold text-white hover:text-orange-500">
            Work Experience ({portfolio.work.length})
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-4">
            <Button
              onClick={addWork}
              variant="outline"
              className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
            >
              <Plus size={18} className="mr-2" />
              Add Experience
            </Button>
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {portfolio.work.map((job, index) => (
                <div key={index} className="p-4 bg-zinc-900/50 rounded-xl space-y-3 border border-orange-500/10">
                  <div className="flex justify-between items-start">
                    <h4 className="text-orange-500 font-semibold">Experience #{index + 1}</h4>
                    <Button
                      onClick={() => removeWork(index)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Position"
                      value={job.position}
                      onChange={(e) => updateWork(index, 'position', e.target.value)}
                      className="bg-zinc-900 border-orange-500/20 text-white"
                    />
                    <Input
                      placeholder="Company"
                      value={job.company}
                      onChange={(e) => updateWork(index, 'company', e.target.value)}
                      className="bg-zinc-900 border-orange-500/20 text-white"
                    />
                  </div>
                  <Input
                    placeholder="Location"
                    value={job.location}
                    onChange={(e) => updateWork(index, 'location', e.target.value)}
                    className="bg-zinc-900 border-orange-500/20 text-white"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Start Date (YYYY-MM)"
                      value={job.startDate}
                      onChange={(e) => updateWork(index, 'startDate', e.target.value)}
                      className="bg-zinc-900 border-orange-500/20 text-white"
                    />
                    <Input
                      placeholder="End Date (or leave empty)"
                      value={job.endDate || ''}
                      onChange={(e) => updateWork(index, 'endDate', e.target.value || null)}
                      className="bg-zinc-900 border-orange-500/20 text-white"
                    />
                  </div>
                  
                  {/* Highlights/Bullet Points */}
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-300 text-sm">Bullet Points / Highlights</Label>
                      <Button
                        onClick={() => addHighlight(index)}
                        variant="outline"
                        size="sm"
                        className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10 h-7 text-xs"
                      >
                        <Plus size={14} className="mr-1" />
                        Add Bullet
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(job.highlights || []).map((highlight, hIndex) => (
                        <div key={hIndex} className="flex gap-2 items-start">
                          <span className="text-orange-500 mt-2 text-sm">•</span>
                          <Textarea
                            placeholder="Describe an achievement or responsibility..."
                            value={highlight}
                            onChange={(e) => updateHighlight(index, hIndex, e.target.value)}
                            rows={2}
                            className="bg-zinc-900 border-orange-500/20 text-white text-sm flex-1"
                          />
                          <Button
                            onClick={() => removeHighlight(index, hIndex)}
                            variant="outline"
                            size="icon"
                            className="border-red-500/30 text-red-500 hover:bg-red-500/10 h-8 w-8 shrink-0"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ))}
                      {(!job.highlights || job.highlights.length === 0) && (
                        <p className="text-gray-500 text-sm italic">No bullet points added yet. Click "Add Bullet" to add achievements or responsibilities.</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default PortfolioEditor;
